<template>
  <ValidationObserver ref="questions">
    <div v-if="isLoadingApplicationAnswers">
      <v-skeleton-loader type="list-item-three-line" />
    </div>
    <div v-else>
      <div v-for="(questionItem, index) in questionsWithAnswers" :key="index">
        <div
          v-if="
            shouldStepViewBeShown(index) || ifStepHasAlreadyBeenCompleted(index)
          "
          v-show="shouldStepViewBeShown(index)"
          class="mt-2"
        >
          <span class="questions__section">
            Application Questions
          </span>
          <h2 class="questions__question">
            {{ questionItem.question }}
          </h2>
          <h3 class="questions__explanation" v-if="questionItem.explanation">
            {{ questionItem.explanation }}
          </h3>
          <component
            @onChange="handleChange"
            :is="getQuestionTypeComponent(questionItem)"
            :ref="getQuestionRef(questionItem)"
            :question="questionItem"
            :showValidationErrors="showValidationErrors"
          />
        </div>
      </div>
    </div>
    <div class="d-flex justify-end">
      <PrimaryButton @click.native="nextStep" :loading="isLoading">
        Continue
      </PrimaryButton>
    </div>
  </ValidationObserver>
</template>

<script>
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import { APPLICATION_NAMESPACE } from "@/store/modules/applications";
import {
  ADD_APPLICATION_ANSWER,
  EDIT_APPLICATION_ANSWER,
  FETCH_CURRENT_APPLICATION_ANSWERS
} from "@/store/modules/applications/actions";
import { createNamespacedHelpers } from "vuex";
import Radio from "@/views/profile/QuestionsAndAnswers/QuestionTypes/Radio";
import DatePicker from "@/views/profile/QuestionsAndAnswers/QuestionTypes/Date";
import File from "@/views/profile/QuestionsAndAnswers/QuestionTypes/File";
import Checkbox from "@/views/profile/QuestionsAndAnswers/QuestionTypes/Checkbox";
import Select from "@/views/profile/QuestionsAndAnswers/QuestionTypes/Select";
import Textarea from "@/views/profile/QuestionsAndAnswers/QuestionTypes/Textarea";
import TextField from "@/views/profile/QuestionsAndAnswers/QuestionTypes/TextField";
import { ValidationObserver } from "vee-validate";
import { size, map, filter, first } from "lodash";
import {
  GET_CURRENT_APPLICATION_ANSWERS,
  GET_IS_FETCHING_CURRENT_APPLICATION_ANSWERS,
  GET_CURRENT_USER_APPLICATION
} from "@/store/modules/applications/getters";

const {
  mapActions: mapApplicationActions,
  mapGetters: mapApplicationGetters
} = createNamespacedHelpers(APPLICATION_NAMESPACE);

export default {
  components: {
    PrimaryButton,
    Radio,
    Date,
    Checkbox,
    Select,
    Textarea,
    TextField,
    ValidationObserver
  },
  props: {
    items: {
      type: Array,
      required: true
    }
  },
  async created() {
    this.emitProgressValue({ step: this.steps.current });
    await this.fetchCurrentApplicationAnswers(this.currentUserApplication.id);
    this.adjustExistingAnswers();
  },
  data() {
    return {
      steps: {
        current: 1,
        saved: 0
      },
      showValidationErrors: false,
      currentStepData: null,
      alreadyFilledQuestion: [],
      isLoading: false,
      questionsWithAnswers: []
    };
  },
  computed: {
    ...mapApplicationGetters({
      currentUserApplication: GET_CURRENT_USER_APPLICATION,
      currentApplicationAnswers: GET_CURRENT_APPLICATION_ANSWERS,
      isLoadingApplicationAnswers: GET_IS_FETCHING_CURRENT_APPLICATION_ANSWERS
    }),
    currentBookingId() {
      return this.$route.params.id;
    },
    totalSteps() {
      return size(this.items);
    },
    currentQuestion() {
      return this.items[this.steps.current - 1];
    },
    progressValue() {
      return (this.steps.current / this.totalSteps) * 100;
    },
    // needed value for parent form component
    showBackButton() {
      return this.steps.current > 1;
    }
  },
  methods: {
    ...mapApplicationActions({
      addApplicationAnswer: ADD_APPLICATION_ANSWER,
      editApplicationAnswer: EDIT_APPLICATION_ANSWER,
      fetchCurrentApplicationAnswers: FETCH_CURRENT_APPLICATION_ANSWERS
    }),
    emitProgressValue({ step }) {
      this.$emit("onStepChanged", {
        progressValue: (step / this.totalSteps) * 100
      });
    },
    async uploadFiles(answerId) {
      await this.$refs.file[0].uploadFiles(answerId);
    },
    adjustExistingAnswers() {
      this.questionsWithAnswers = map(this.items, question => {
        return {
          ...question,
          answerData: first(
            filter(this.currentApplicationAnswers, answer => {
              return question.id === answer.question.id;
            })
          )
        };
      });
    },
    getQuestionRef({ inputType }) {
      return inputType === "file" ? "file" : "";
    },
    getQuestionTypeComponent({ inputType }) {
      switch (inputType) {
        case "radio":
          return Radio;
        case "select":
          return Select;
        case "checkbox":
          return Checkbox;
        case "text":
          return TextField;
        case "textarea":
          return Textarea;
        case "date":
          return DatePicker;
        case "file":
          return File;
      }
    },
    onClose() {
      this.$emit("close");
    },
    stepForward() {
      this.currentStepData = null;
      if (
        this.steps.saved < this.totalSteps &&
        this.steps.current > this.steps.saved
      ) {
        this.steps.saved += 1;
      }
      this.emitProgressValue({ step: this.steps.current + 1 });
      if (this.steps.current < this.totalSteps) this.steps.current += 1;
    },
    stepBack() {
      this.currentStepData = null;
      this.emitProgressValue({ step: this.steps.current - 1 });
      if (this.steps.current > 1) {
        this.steps.current--;
      }
    },
    shouldStepViewBeShown(index) {
      const viewStep = index + 1;
      return viewStep <= this.totalSteps && this.steps.current === viewStep;
    },
    ifStepHasAlreadyBeenCompleted(index) {
      const viewStep = index + 1;
      return viewStep <= this.steps.saved;
    },
    async nextStep() {
      const isValid = await this.$refs.questions.validate();
      this.showValidationErrors = true;
      if (isValid) {
        this.isLoading = true;
        if (this.isCurrentStepDataValid()) {
          if (this.ifCurrentStepAnswerExist()) {
            await this.onEditAnswer();
          } else {
            await this.onAddAnswer();
          }
        }
        this.stepForward();
        this.showValidationErrors = false;
        this.isLoading = false;
      }
    },
    isCurrentStepDataValid() {
      return (
        this.currentStepData &&
        (size(this.currentStepData.answers) || size(this.currentStepData.files))
      );
    },
    async onEditAnswer() {
      const answerId = this.alreadyFilledQuestion[
        this.currentStepData.question.id
      ];

      const {
        meta: { canContinue }
      } = await this.editApplicationAnswer({
        answerId,
        data: {
          ...this.currentStepData,
          application: {
            id: this.currentUserApplication.id
          }
        }
      });
      if (this.currentStepData.files) {
        await this.uploadFiles(answerId);
      }
      if (!canContinue) {
        return this.stopProcess();
      }
    },
    async onAddAnswer() {
      const {
        data: { id: answerId },
        meta: { canContinue }
      } = await this.addApplicationAnswer({
        ...this.currentStepData,
        application: {
          id: this.currentUserApplication.id
        }
      });
      if (this.currentStepData.files) {
        await this.uploadFiles(answerId);
      }
      if (!canContinue) {
        return this.stopProcess();
      }
      this.alreadyFilledQuestion[this.currentStepData.question.id] = answerId;
    },
    ifCurrentStepAnswerExist() {
      return this.alreadyFilledQuestion[this.currentStepData.question.id];
    },
    handleChange(currentStepData) {
      this.currentStepData = currentStepData;
    },
    stopProcess() {
      return this.$router.push("dont-meet-requirements");
    }
  }
};
</script>

<style lang="scss">
.questions {
  &__section {
    float: left;
    width: 100%;
    margin-top: 0px;
    font-weight: bold;
  }

  &__question {
    margin-top: 10px;
  }

  &__explanation {
    margin-top: 5px;
    font-weight: normal;
    line-height: 1.3;
  }
}
</style>
